import { useMemo, ReactNode } from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  gql,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { useMsal, IMsalContext } from '@azure/msal-react';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import Config from 'config';
import { getAccessToken } from 'config/auth';
import { Header } from '@lwtears/lwt-common-frontend/lib/@common/util/header-util';
import {
  extractAuthHeadersFromCookies,
  hasAuthCookies,
  removeAuthCookies,
} from 'util/auth-cookie-util';
import { sendGraphErrorLog, formatErrors } from 'util/telemetry-util';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { createUploadLink } from 'apollo-upload-client';
import initApolloStore from 'graph/init-apollo-global-store';

const {
  REACT_APP_APP_NAME: appName,
  REACT_APP_APP_VERSION: appVersion,
  REACT_APP_BUILD_NUMBER: appBuild,
} = process.env;

export const setupClient = (azureContext: IMsalContext): ApolloClient<NormalizedCacheObject> => {
  const cache = new InMemoryCache({
    possibleTypes: {
      Assessment: ['BenchmarkAssessment', 'UnitAssessment'],
      Checklist: ['BenchmarkChecklist', 'UnitChecklist', 'WeeklyChecklist'],
    },
    typePolicies: {
      BenchmarkAssessmentDetails: {
        keyFields: ['assessmentId', 'productCurriculumId', 'period'],
      },
      BenchmarkChecklistDetails: {
        keyFields: ['checklistId', 'productCurriculumId', 'period'],
      },
    },
  });
  const authLink = setContext(async () => {
    let authHeaders;

    if (hasAuthCookies()) {
      authHeaders = extractAuthHeadersFromCookies();
    } else {
      const response = await getAccessToken(azureContext);
      authHeaders = { ...(response && { Authorization: `Bearer ${response.accessToken}` }) };
    }

    const locale = getLocale();
    const headers = {
      headers: {
        [Header.APP]: appName,
        [Header.VERSION]: appVersion,
        [Header.BUILD]: appBuild || 'dev',
        [Header.LOCALE]: locale,
        ...authHeaders,
      },
    };

    return headers;
  });

  const link = createUploadLink({
    uri: Config.graphUrl,
  });

  const errorLink = onError(({ graphQLErrors = [], networkError }) => {
    if ((networkError as ServerError)?.statusCode === 401) {
      removeAuthCookies();
      azureContext.instance.logoutRedirect();
    }

    // don't try to log if no internet
    if (!(networkError as ServerError)?.statusCode && !graphQLErrors.length) return;

    // log non-network-failure, non-auth related errors
    const errorEvent = formatErrors(graphQLErrors, networkError);

    try {
      sendGraphErrorLog({
        events: {
          ...(errorEvent ?? {}),
        },
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.log(err);
    }
  });

  const client = new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, errorLink, link]),
    resolvers: {},
  });

  const store = initApolloStore();

  cache.writeQuery({
    query: gql`
      query User {
        user {
          __typename
        }
      }
    `,
    data: store,
  });

  return client;
};

const ApolloProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const azureContext = useMsal();
  const client = useMemo(() => setupClient(azureContext), [azureContext]);

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
};

export default ApolloProvider;
